import { z } from 'zod';

import { isWholeIn, wholeIn } from './fields';

export const TRIGGER_KINDS = ['site', 'page', 'widget_opened', 'advanced', 'rescuer'] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];

/** How a condition's value is typed; decides the operators and the input. */
export type ConditionValueKind =
  'text' | 'number' | 'money' | 'flag' | 'list' | 'days' | 'devices' | 'countries' | 'attribute';

/** Every condition field, grouped as the picker shows them. */
export const CONDITION_GROUPS = {
  page: ['url', 'referrer', 'timeOnPageSeconds', 'timeOnSiteSeconds', 'pageViews', 'exitIntent'],
  visitor: [
    'visits',
    'previousChats',
    'isCustomer',
    'hasOrderInProduction',
    'country',
    'region',
    'city',
    'device',
  ],
  store: ['agentsOnline', 'hourOfDay', 'dayOfWeek'],
  shopping: [
    'cartTotalMinor',
    'cartHasProductTag',
    'cartHasProductType',
    'viewingProductTag',
    'viewingProductAttribute',
    'checkoutStep',
  ],
} as const;

export type ConditionField = (typeof CONDITION_GROUPS)[keyof typeof CONDITION_GROUPS][number];

export const VALUE_KIND: Record<ConditionField, ConditionValueKind> = {
  url: 'text',
  referrer: 'text',
  timeOnPageSeconds: 'number',
  timeOnSiteSeconds: 'number',
  pageViews: 'number',
  exitIntent: 'flag',
  visits: 'number',
  previousChats: 'number',
  isCustomer: 'flag',
  hasOrderInProduction: 'flag',
  country: 'countries',
  region: 'list',
  city: 'list',
  device: 'devices',
  agentsOnline: 'flag',
  hourOfDay: 'number',
  dayOfWeek: 'days',
  cartTotalMinor: 'money',
  cartHasProductTag: 'list',
  cartHasProductType: 'list',
  viewingProductTag: 'list',
  viewingProductAttribute: 'attribute',
  checkoutStep: 'list',
};

/** What the API itself knows; a rescuer runs on the server and may use only these. */
export const SERVER_FIELDS: ReadonlySet<ConditionField> = new Set<ConditionField>([
  'visits',
  'previousChats',
  'isCustomer',
  'hasOrderInProduction',
  'agentsOnline',
  'hourOfDay',
  'dayOfWeek',
  'country',
  'region',
  'city',
  'device',
  'cartTotalMinor',
]);

export const OPS: Record<ConditionValueKind, readonly string[]> = {
  text: ['contains', 'not_contains', 'equals', 'starts_with', 'matches'],
  number: ['gte', 'gt', 'lte', 'lt', 'eq', 'neq'],
  money: ['gte', 'gt', 'lte', 'lt'],
  flag: ['is'],
  list: ['in', 'not_in'],
  days: ['in', 'not_in'],
  devices: ['in', 'not_in'],
  countries: ['in', 'not_in'],
  attribute: ['gte', 'gt', 'lte', 'lt', 'eq', 'neq', 'contains'],
};

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const DEVICES = ['desktop', 'tablet', 'mobile'] as const;
export const MAX_CONDITIONS = 10;
export const MAX_ACTIONS = 5;
export const MAX_SUGGESTED_REPLIES = 4;

const MONEY = /^\d+(?:\.\d{1,3})?$/;

export const isConditionField = (value: string): value is ConditionField => value in VALUE_KIND;

export const conditionSchema = z
  .object({
    field: z.string(),
    op: z.string(),
    /** text, number, money (major units), flag ('true' | 'false') or an attribute's value */
    value: z.string(),
    /** list, days, devices and countries */
    values: z.array(z.string()),
    /** viewingProductAttribute only */
    key: z.string(),
  })
  .superRefine((condition, ctx) => {
    if (!isConditionField(condition.field)) {
      ctx.addIssue({
        code: 'custom',
        path: ['field'],
        message: 'chatSettings:validation.conditionField',
      });
      return;
    }
    const kind = VALUE_KIND[condition.field];
    const value = condition.value.trim();
    if (!OPS[kind].includes(condition.op)) {
      ctx.addIssue({
        code: 'custom',
        path: ['op'],
        message: 'chatSettings:validation.conditionOp',
      });
    }
    const bad = (path: string, message: string) => {
      ctx.addIssue({ code: 'custom', path: [path], message });
    };
    if (kind === 'text' && (value === '' || value.length > 500))
      bad('value', 'chatSettings:validation.conditionText');
    if (kind === 'number') {
      const max = condition.field === 'hourOfDay' ? 23 : 1_000_000;
      if (!isWholeIn(value, 0, max))
        bad(
          'value',
          condition.field === 'hourOfDay'
            ? 'chatSettings:validation.hourOfDay'
            : 'chatSettings:validation.conditionNumber',
        );
    }
    if (kind === 'money' && !MONEY.test(value))
      bad('value', 'chatSettings:validation.conditionMoney');
    if (kind === 'flag' && value !== 'true' && value !== 'false')
      bad('value', 'chatSettings:validation.conditionFlag');
    if (
      (kind === 'list' || kind === 'days' || kind === 'devices' || kind === 'countries') &&
      condition.values.length === 0
    ) {
      bad('values', 'chatSettings:validation.conditionValues');
    }
    if (kind === 'countries' && condition.values.some((code) => !/^[A-Za-z]{2}$/.test(code))) {
      bad('values', 'chatSettings:validation.countryCode');
    }
    if (kind === 'attribute') {
      if (condition.key.trim() === '' || condition.key.trim().length > 60)
        bad('key', 'chatSettings:validation.attributeKey');
      if (value === '' || value.length > 100) bad('value', 'chatSettings:validation.conditionText');
    }
  });

export type ConditionForm = z.infer<typeof conditionSchema>;

export const ACTION_TYPES = ['message', 'wait', 'tag', 'product_card', 'discount'] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

export const actionSchema = z
  .object({
    type: z.enum(ACTION_TYPES),
    body: z.string(),
    /** store: no name; alias: `alias`; member: `asUserId` */
    sendAs: z.enum(['store', 'alias', 'member']),
    alias: z.string(),
    asUserId: z.string(),
    suggestedReplies: z.array(z.string()).max(MAX_SUGGESTED_REPLIES),
    seconds: z.string(),
    tag: z.string(),
    productId: z.string(),
    /** For the row and the preview only; never sent. */
    productTitle: z.string(),
    discountId: z.string(),
  })
  .superRefine((action, ctx) => {
    const bad = (path: string, message: string) => {
      ctx.addIssue({ code: 'custom', path: [path], message });
    };
    if (action.type === 'message') {
      const body = action.body.trim();
      if (body === '') bad('body', 'chatSettings:validation.bodyRequired');
      else if (body.length > 1000) bad('body', 'chatSettings:validation.max1000');
      if (
        action.sendAs === 'alias' &&
        (action.alias.trim() === '' || action.alias.trim().length > 60)
      ) {
        bad('alias', 'chatSettings:validation.alias');
      }
      if (action.sendAs === 'member' && action.asUserId === '')
        bad('asUserId', 'chatSettings:validation.member');
    }
    if (action.type === 'wait' && !isWholeIn(action.seconds, 1, 120))
      bad('seconds', 'chatSettings:validation.waitSeconds');
    if (action.type === 'tag' && (action.tag.trim() === '' || action.tag.trim().length > 60))
      bad('tag', 'chatSettings:validation.tag');
    if (action.type === 'product_card' && action.productId === '')
      bad('productId', 'chatSettings:validation.product');
    if (action.type === 'discount' && action.discountId === '')
      bad('discountId', 'chatSettings:validation.discount');
  });

export type ActionForm = z.infer<typeof actionSchema>;

export const triggerSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'chatSettings:validation.nameRequired' })
      .max(120, { error: 'chatSettings:validation.max120' }),
    kind: z.enum(TRIGGER_KINDS),
    enabled: z.boolean(),
    oncePerVisitor: z.boolean(),
    rescueAfterSeconds: z.string(),
    position: wholeIn(0, 10_000, 'chatSettings:validation.position'),
    conditions: z.array(conditionSchema).max(MAX_CONDITIONS),
    actions: z.array(actionSchema).min(1).max(MAX_ACTIONS),
  })
  .superRefine((trigger, ctx) => {
    if (trigger.kind === 'page' && !trigger.conditions.some((c) => c.field === 'url')) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditions'],
        message: 'chatSettings:validation.pageNeedsUrl',
      });
    }
    if (trigger.kind === 'rescuer') {
      if (!isWholeIn(trigger.rescueAfterSeconds, 30, 3600)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rescueAfterSeconds'],
          message: 'chatSettings:validation.rescueAfter',
        });
      }
      trigger.conditions.forEach((condition, index) => {
        if (isConditionField(condition.field) && !SERVER_FIELDS.has(condition.field)) {
          ctx.addIssue({
            code: 'custom',
            path: ['conditions', index, 'field'],
            message: 'chatSettings:validation.rescuerField',
          });
        }
      });
    }
    if (!trigger.actions.some((action) => action.type === 'message')) {
      ctx.addIssue({
        code: 'custom',
        path: ['actions'],
        message: 'chatSettings:validation.needsMessage',
      });
    }
    if (trigger.actions.filter((action) => action.type === 'discount').length > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['actions'],
        message: 'chatSettings:validation.oneDiscount',
      });
    }
  });

export type TriggerForm = z.infer<typeof triggerSchema>;
